'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { MessageCircle, Phone, Video, Clock, CheckCircle2, ChevronRight, User, Loader2 } from 'lucide-react';
import { useConcierge } from '@/hooks/useConcierge';
import { MessageModal } from './MessageModal';
import type { AssignedAgent, ActiveSearch } from '@/types/customer';

interface ConciergeWidgetProps {
  /** Override agent data (if not using hook) */
  agent?: AssignedAgent;
  /** Override active searches (if not using hook) */
  activeSearches?: ActiveSearch[];
  /** Whether to fetch data from API (default: true) */
  useLiveData?: boolean;
}

export const ConciergeWidget: React.FC<ConciergeWidgetProps> = ({
  agent: propAgent,
  activeSearches: propActiveSearches,
  useLiveData = true,
}) => {
  // State for message modal
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const pathname = usePathname();
  const localeMatch = pathname.match(/^\/([a-z]{2})(\/|$)/);
  const locale = localeMatch ? localeMatch[1] : 'en';
  
  // Fetch live data from API if enabled
  const { agent: liveAgent, activeSearches: liveActiveSearches, isLoading } = useConcierge();
  
  // Use prop data if provided, otherwise use live data
  const agent = propAgent || (useLiveData ? liveAgent : null);
  const activeSearches = propActiveSearches || (useLiveData ? liveActiveSearches : []);
  
  // Extract display values with fallbacks
  const agentName = agent?.name || 'Your Concierge';
  const agentTitle = agent?.title || 'Private Client Advisor';
  const agentImage = agent?.avatarUrl;
  const isOnline = agent?.isOnline ?? false;
  const replyTime = agent?.replyTime || '< 1h';
  
  // Get first active search for display
  const activeSearch = activeSearches[0];
  
  // Loading state
  if (isLoading && useLiveData) {
    return (
      <Card
        className="!bg-stone-900 text-white h-full flex items-center justify-center !border-stone-800 relative overflow-hidden !p-8 md:!p-10"
        noPadding
      >
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
          <p className="text-stone-400 text-sm">Loading your concierge...</p>
        </div>
      </Card>
    );
  }
  
  // No agent assigned state
  if (!agent && useLiveData) {
    return (
      <>
        <Card
          className="!bg-stone-900 text-white h-full flex flex-col justify-center !border-stone-800 relative overflow-hidden group !p-8 md:!p-10"
          noPadding
        >
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-stone-800/40 to-stone-900/0 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />
          
          <div className="relative z-10 flex flex-col items-center text-center p-8">
            <div className="w-20 h-20 rounded-full bg-stone-800 border-2 border-stone-700 flex items-center justify-center mb-6">
              <User size={32} className="text-stone-500" />
            </div>
            <h3 className="font-semibold text-xl text-white mb-2">No Concierge Assigned</h3>
            <p className="text-stone-400 text-sm max-w-xs mb-6">
              A dedicated advisor will be assigned to you soon. In the meantime, feel free to reach out through our main contact.
            </p>
            <Button
              onClick={() => setIsMessageModalOpen(true)}
              className="!bg-white hover:!bg-stone-200 !text-stone-900 border-none h-12 flex items-center justify-center gap-3 shadow-lg !rounded-2xl"
            >
              <MessageCircle size={20} className="text-stone-600" />
              <span className="font-semibold">Send a Message</span>
            </Button>
          </div>
        </Card>
        
        {/* Message Modal */}
        <MessageModal
          isOpen={isMessageModalOpen}
          onClose={() => setIsMessageModalOpen(false)}
          recipientName="Allys Properties"
        />
      </>
    );
  }
  return (
    <Card
      className="!bg-stone-900 text-white h-full flex flex-col justify-between !border-stone-800 relative overflow-hidden group !p-8 md:!p-10"
      noPadding
    >
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-stone-800/40 to-stone-900/0 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />

      <div className="relative z-10 flex flex-col h-full p-8 md:p-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-5">
            <div className="relative">
              <div className="w-16 h-16 rounded-full p-0.5 bg-gradient-to-tr from-amber-200 to-amber-600">
                {agentImage ? (
                  <Image
                    src={agentImage}
                    alt={agentName}
                    width={64}
                    height={64}
                    className="w-full h-full rounded-full object-cover border-4 border-stone-900"
                  />
                ) : (
                  <div className="w-full h-full rounded-full bg-stone-800 border-4 border-stone-900 flex items-center justify-center">
                    <User size={28} className="text-stone-400" />
                  </div>
                )}
              </div>
              {isOnline && (
                <span className="absolute bottom-1 right-1 w-4 h-4 bg-green-500 border-2 border-stone-900 rounded-full animate-pulse" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-xl leading-tight text-white">{agentName}</h3>
              <p className="text-stone-400 text-sm font-medium">{agentTitle}</p>
            </div>
          </div>
          <div className="bg-stone-800/80 backdrop-blur-sm px-4 py-1.5 rounded-full text-xs font-medium text-stone-300 flex items-center gap-2 border border-stone-700 shadow-lg w-fit">
            <Clock size={14} className="text-amber-400" />
            <span>Replies in {replyTime}</span>
          </div>
        </div>

        {/* Context/Status */}
        {activeSearch ? (
          <div className="mb-auto p-5 bg-gradient-to-b from-stone-800/50 to-stone-800/20 rounded-3xl border border-stone-800/50 backdrop-blur-md">
            <div className="flex justify-between items-center mb-3">
              <p className="text-xs text-stone-500 uppercase tracking-wider font-bold">Active Search</p>
              {activeSearch.updatesAvailable && (
                <div className="flex items-center gap-1 text-green-400 text-xs font-medium">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                  Updates Available
                </div>
              )}
            </div>

            <div className="flex items-start gap-3 mb-2">
              <CheckCircle2 size={18} className="text-amber-500 mt-0.5 shrink-0" />
              <span className="text-sm font-medium text-stone-100">{activeSearch.name}</span>
            </div>
            {activeSearch.message && (
              <p className="text-sm text-stone-400 pl-7 leading-relaxed mb-4">{activeSearch.message}</p>
            )}
            {activeSearch.matchCount && activeSearch.matchCount > 0 && (
              <p className="text-sm text-stone-400 pl-7 leading-relaxed mb-4">
                {activeSearch.matchCount} properties match your criteria.
              </p>
            )}
            <div className="pl-7">
              <Link
                href={`/${locale}/account/wishlists`}
                className="text-xs font-medium text-white flex items-center gap-1 hover:gap-2 transition-all"
              >
                View Shortlist <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        ) : (
          <div className="mb-auto p-5 bg-gradient-to-b from-stone-800/50 to-stone-800/20 rounded-3xl border border-stone-800/50 backdrop-blur-md">
            <div className="flex justify-between items-center mb-3">
              <p className="text-xs text-stone-500 uppercase tracking-wider font-bold">No Active Searches</p>
            </div>
            <p className="text-sm text-stone-400 leading-relaxed">
              Start a property search and your concierge will curate personalized recommendations for you.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-8 space-y-4">
          {agent?.whatsapp ? (
            <a
              href={`https://wa.me/${agent.whatsapp.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full"
            >
              <Button
                fullWidth
                className="!bg-white hover:!bg-stone-200 !text-stone-900 border-none h-14 flex items-center justify-center gap-3 shadow-lg group/btn !rounded-2xl"
              >
                <MessageCircle size={22} className="text-[#25D366] fill-current" />
                <span className="font-semibold text-base">Connect on WhatsApp</span>
              </Button>
            </a>
          ) : (
            <Button
              fullWidth
              onClick={() => setIsMessageModalOpen(true)}
              className="!bg-white hover:!bg-stone-200 !text-stone-900 border-none h-14 flex items-center justify-center gap-3 shadow-lg group/btn !rounded-2xl"
            >
              <MessageCircle size={22} className="text-stone-600" />
              <span className="font-semibold text-base">Send a Message</span>
            </Button>
          )}

          <div className="grid grid-cols-2 gap-4">
            {agent?.phone ? (
              <a href={`tel:${agent.phone}`}>
                <Button
                  fullWidth
                  variant="secondary"
                  className="!bg-stone-800 !text-stone-300 hover:!bg-stone-700 hover:!text-white !border-stone-700 h-12 !rounded-xl"
                >
                  <Phone size={18} className="mr-2" />
                  Call Agent
                </Button>
              </a>
            ) : (
              <Button
                variant="secondary"
                className="!bg-stone-800 !text-stone-300 hover:!bg-stone-700 hover:!text-white !border-stone-700 h-12 !rounded-xl opacity-50 cursor-not-allowed"
                disabled
              >
                <Phone size={18} className="mr-2" />
                Call Agent
              </Button>
            )}
            <Button
              variant="secondary"
              className="!bg-stone-800 !text-stone-500 !border-stone-700 h-12 !rounded-xl opacity-50 cursor-not-allowed"
              disabled
              title="Video calling coming soon"
            >
              <Video size={18} className="mr-2" />
              Video Call
            </Button>
          </div>
        </div>
      </div>
      
      {/* Message Modal */}
      <MessageModal
        isOpen={isMessageModalOpen}
        onClose={() => setIsMessageModalOpen(false)}
        recipientName={agent?.name || 'Allys Properties'}
      />
    </Card>
  );
};
